import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, XCircle, ShieldCheck, RefreshCw, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusPill } from "@/components/uiext";
import { toast } from "sonner";

/**
 * Recent logins panel — shown in Settings → Account / Security.
 * Pulls from /api/auth/recent-logins (last 30 attempts including failures).
 *
 * Doubles as chargeback evidence: a customer disputing the charge can be
 * shown an export of this list (timestamps + IPs prove they used the
 * service).
 */
export function RecentLoginsPanel() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/recent-logins");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const exportCsv = () => {
    const rows = [
      ["timestamp", "success", "ip", "user_agent"],
      ...items.map((i) => [
        i.ts,
        i.success ? "true" : "false",
        i.ip || "",
        (i.user_agent || "").replace(/"/g, '""'),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `elswitch-recent-logins-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card data-testid="recent-logins-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[hsl(var(--accent))]" />
            <h3 className="text-sm font-semibold">
              {t("recentLogins.title", "Recent logins")}
            </h3>
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={load}
              disabled={loading}
              data-testid="recent-logins-refresh"
              title={t("common.refresh", "Refresh")}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={exportCsv}
              disabled={!items.length}
              data-testid="recent-logins-export"
            >
              {t("recentLogins.export", "Export CSV")}
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          {t(
            "recentLogins.hint",
            "Last 30 sign-in attempts (success + failures). If you don't recognize one, change your password immediately."
          )}
        </p>
        {loading ? (
          <p className="text-xs text-muted-foreground">{t("common.loading", "Loading…")}</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            {t("recentLogins.empty", "No login records yet.")}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">{t("common.ts", "Time")}</TableHead>
                <TableHead className="text-xs">{t("recentLogins.result", "Result")}</TableHead>
                <TableHead className="text-xs">{t("recentLogins.ip", "IP")}</TableHead>
                <TableHead className="text-xs">{t("recentLogins.ua", "User agent")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.id} className="row-hover">
                  <TableCell className="text-xs">
                    {new Date(i.ts).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {i.success ? (
                      <StatusPill kind="ok">
                        <CheckCircle2 className="h-3 w-3 mr-1 inline" />
                        {t("recentLogins.success", "success")}
                      </StatusPill>
                    ) : (
                      <StatusPill kind="danger">
                        <XCircle className="h-3 w-3 mr-1 inline" />
                        {t("recentLogins.failed", "failed")}
                      </StatusPill>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-[11px]">
                    {i.ip || "—"}
                  </TableCell>
                  <TableCell className="text-[10px] text-muted-foreground line-clamp-1 max-w-[300px]">
                    {i.user_agent || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Banner shown across tenant pages when the email is not yet verified.
 * Provides "Resend verification" action.
 */
export function UnverifiedEmailBanner() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
  }, []);

  if (!user || user.scope !== "tenant" || user.user?.email_verified || dismissed) {
    return null;
  }

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success(
        t("recentLogins.resentMail", "Verification email re-sent.")
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-md border border-[hsl(var(--warn))]/40 bg-[hsl(var(--warn))]/5 px-3 py-2 mb-4 flex items-center gap-3 flex-wrap"
      data-testid="unverified-email-banner"
    >
      <Mail className="h-4 w-4 text-[hsl(var(--warn))]" />
      <span className="text-xs">
        {t(
          "recentLogins.unverified",
          "Your email isn't verified yet. Some billing-related actions are restricted until you confirm the link we sent."
        )}
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={resend}
        disabled={busy}
        data-testid="unverified-resend-btn"
      >
        {busy ? "…" : t("recentLogins.resend", "Resend email")}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setDismissed(true)}
        data-testid="unverified-dismiss-btn"
      >
        {t("common.dismiss", "Dismiss")}
      </Button>
    </div>
  );
}

export default RecentLoginsPanel;
